import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { LanguagePreferenceService } from 'app/core/transloco/language-preference.service';
import { SaiLang } from 'app/core/transloco/language-preference.util';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages',
    standalone: true,
    imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslocoModule],
})
export class LanguagesComponent implements OnInit, OnDestroy {
    activeLang: SaiLang;
    lang: string = 'en';
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _translocoService = inject(TranslocoService);
    private readonly _languagePreferenceService = inject(LanguagePreferenceService);
    private readonly _unsubscribeAll = new Subject<void>();

    ngOnInit(): void {
        const storedLang = this._languagePreferenceService.getPersisted();
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((res) => {
                this.lang = res;
                this._changeDetectorRef.markForCheck();
            });

        this.activeLang = storedLang;

        // Set language only once at initialization
        this._languagePreferenceService.apply(storedLang);
        setTimeout(() => {
            this._updateNavigation(storedLang);
        }, 100);

        // Subscribe to language changes
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((activeLang) => {
                // Transloco types `langChanges$` as `string`; this app only ever registers the
                // two SaiLang codes, so the narrowing happens here rather than in the handler
                // signature, which strictFunctionTypes will not accept.
                const lang = activeLang as SaiLang;
                if (this.activeLang !== lang) {
                    this.activeLang = lang;
                    this._updateNavigation(lang);
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    setActiveLang(lang: SaiLang): void {
        if (this.activeLang !== lang) {
            this.activeLang = lang;
            this._languagePreferenceService
                .savePreferredLanguage(lang)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe();
            this._updateNavigation(lang);
            this._changeDetectorRef.markForCheck();
        }
    }

    /** Binary toggle — the app only ever has English and Arabic. */
    toggleLanguage(): void {
        this.setActiveLang(this.activeLang === 'ar' ? 'en' : 'ar');
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private _updateNavigation(lang: string): void {
        // Navigation component now handles translations automatically
        // No manual update needed
    }
}
